import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

import { cn } from "@/lib/cn";

const DISMISS_KEY = "ai-pwa-dismissed";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export function InstallBanner() {
  const [evt, setEvt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(DISMISS_KEY) === "1") return;

    function onPrompt(e: Event) {
      e.preventDefault();
      setEvt(e as BeforeInstallPromptEvent);
    }
    function onInstalled() {
      setInstalled(true);
      setEvt(null);
    }
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !evt) return null;

  async function handleInstall() {
    if (!evt) return;
    try {
      await evt.prompt();
      const { outcome } = await evt.userChoice;
      if (outcome === "accepted") setInstalled(true);
    } catch {
      // User dismissed natively — leave banner so they can retry.
    } finally {
      setEvt(null);
    }
  }

  function dismiss() {
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // Ignore (private mode); banner re-shows on next session.
    }
    setEvt(null);
  }

  return (
    <div
      role="dialog"
      aria-label="Install AI Logistics"
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 sm:bottom-auto sm:top-3 sm:inset-x-auto sm:right-4",
        "sm:max-w-md",
      )}
    >
      <div
        className={cn(
          "mx-3 sm:mx-0 mb-3 sm:mb-0 rounded-lg border border-border bg-white shadow-lg",
          "px-4 py-3 flex items-start gap-3",
        )}
      >
        <div className="size-9 shrink-0 inline-flex items-center justify-center rounded-md bg-accent-50 text-accent-700">
          <Download className="size-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-navy-900">Install AI Logistics</div>
          <div className="mt-0.5 text-xs text-neutral-600">
            Add the portal to your home screen for one-tap access.
          </div>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void handleInstall()}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md bg-accent-600 hover:bg-accent-700",
                "px-3 py-2 min-h-[40px] text-xs font-medium text-white",
              )}
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className={cn(
                "inline-flex items-center rounded-md border border-border bg-white hover:bg-neutral-50",
                "px-3 py-2 min-h-[40px] text-xs font-medium text-neutral-700",
              )}
            >
              Not now
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          className="size-9 inline-flex items-center justify-center rounded-md text-neutral-400 hover:text-navy-800 hover:bg-neutral-100"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}
